/* eslint-disable react/button-has-type */
import React from 'react';
import { QuizPage, TrackCatalogEntry, UserProfile } from '../../types/quiz';

// Import all page components
import ImageSelectionGridPage from './ImageSelectionGridPage';
import ImageContentPage from './ImageContentPage';
import TextInputPage from './TextInputPage';
import NumberInputPage from './NumberInputPage';
import SingleChoicePage from './SingleChoicePage';
import ScalePage from './ScalePage';
import ScaleGroupPage from './ScaleGroupPage';
import TrackSelectPage from './TrackSelectPage';
import MultipleChoicePage from './MultipleChoicePage';
import LoadingPage from './LoadingPage';
import InfoContentPage from './InfoContentPage';
import InsightRevealPage from './InsightRevealPage';
import MeasurementInputPage from './MeasurementInputPage';
import ResultDisplayPage from './ResultDisplayPage';
import TestimonialPage from './TestimonialPage';
import TransformationGalleryPage from './TransformationGalleryPage';
import EmailInputPage from './EmailInputPage';
import StatsDisplayPage from './StatsDisplayPage';
import ScratchCardPage from './ScratchCardPage';
import SalesPage from './SalesPage';
import PaywallStep, { isPaywallPage } from '../../paywall';

interface PageRendererProps {
    page: QuizPage;
    userProfile: UserProfile;
    onAnswer: (value: any, extra?: Record<string, any>) => void;
    onAnswerBatch?: (fields: Record<string, any>) => void;
    onNext: () => void;
    quizId?: string;
    pages?: QuizPage[];
    tracks?: Record<string, TrackCatalogEntry>;
    onBack?: () => void;
}

const PageRenderer: React.FC<PageRendererProps> = ({
    page,
    userProfile,
    onAnswer,
    onAnswerBatch,
    onNext,
    quizId,
    pages,
    tracks,
    onBack,
}) => {
    // The paywall funnel owns its own chrome (its screens are full-bleed and
    // carry their own back affordance), so it is dispatched before the switch
    // rather than as five near-identical cases inside it.
    if (isPaywallPage(page.type)) {
        return (
            <PaywallStep
                page={page}
                userProfile={userProfile}
                pages={pages}
                quizId={quizId}
                onNext={onNext}
                onBack={onBack}
            />
        );
    }

    // Route to correct page component based on type
    switch (page.type) {
        case 'image_selection_grid':
            return <ImageSelectionGridPage page={page} userProfile={userProfile} onAnswer={onAnswer} />;

        case 'image_content':
            return <ImageContentPage page={page} onNext={onNext} />;

        case 'text_input':
            return <TextInputPage page={page} userProfile={userProfile} onAnswer={onAnswer} />;

        case 'number_input':
            return <NumberInputPage page={page} userProfile={userProfile} onAnswer={onAnswer} />;

        case 'single_choice':
            return <SingleChoicePage page={page} userProfile={userProfile} onAnswer={onAnswer} />;

        case 'scale':
            return <ScalePage page={page} userProfile={userProfile} onAnswer={onAnswer} />;

        case 'scale_group':
            return (
                <ScaleGroupPage
                    page={page}
                    userProfile={userProfile}
                    onAnswerBatch={onAnswerBatch}
                    onAnswer={onAnswer}
                />
            );

        case 'track_select':
            return <TrackSelectPage page={page} userProfile={userProfile} tracks={tracks} onAnswer={onAnswer} />;

        case 'track_quiz_slot':
            // Placeholder — QuizController skips/splices this once a track is chosen.
            return null;

        case 'multiple_choice':
            return <MultipleChoicePage page={page} userProfile={userProfile} onAnswer={onAnswer} />;

        case 'loading':
            return <LoadingPage page={page} onNext={onNext} />;

        case 'info_content':
            return (page as any).mode ? (
                <InsightRevealPage page={page} userProfile={userProfile} pages={pages || []} onNext={onNext} />
            ) : (
                <InfoContentPage page={page} onNext={onNext} />
            );

        case 'measurement_input':
            return <MeasurementInputPage page={page} userProfile={userProfile} onAnswer={onAnswer} />;

        case 'result_display':
            return <ResultDisplayPage page={page} userProfile={userProfile} onNext={onNext} />;

        case 'testimonial':
            return <TestimonialPage page={page} onNext={onNext} />;

        case 'transformation_gallery':
            return <TransformationGalleryPage page={page} onNext={onNext} />;

        case 'email_input':
            return <EmailInputPage page={page} userProfile={userProfile} onAnswer={onAnswer} />;

        case 'stats_display':
            return <StatsDisplayPage page={page} onNext={onNext} />;

        case 'scratch_card':
            return <ScratchCardPage page={page} onNext={onNext} />;

        case 'sales':
            return <SalesPage page={page} userProfile={userProfile} onNext={onNext} quizId={quizId} />;

        default:
            return (
                <div className="page-error">
                    <p>Unknown page type: {(page as any).type}</p>
                    <button onClick={onNext}>Continue</button>
                </div>
            );
    }
};

export default PageRenderer;
